import {
  Stack, Text, Box, Card, Group, SimpleGrid, MultiSelect, Checkbox,
  Button, Badge, Divider, Skeleton, Alert, ActionIcon, Tooltip,
  Modal, TextInput, Textarea, SegmentedControl, Select, Paper,
  UnstyledButton,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useEffect, useMemo } from 'react'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import { IconKey, IconPlus, IconEdit, IconTrash, IconEye, IconShieldCheck, IconUsers, IconUserCheck, IconX, IconLock, IconInfoCircle, IconBuildingCommunity, IconSearch, IconCopy } from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { api } from '@/api/client'
import {
  permissionsApi, iconForKey, templateScopeSummary,
  type Permission, type PermissionTemplate, type PermissionResourceMeta,
  type PermissionSensitivity, type TemplateCap,
} from '@/api/permissions'
import { permissionLabel } from '@/api/permissionDisplay'
import { useFeatureFlag } from '@/hooks/useFeatureFlags'
import { awsOrgProvisioningApi, type AwsOuNode } from '@/api/awsOrgProvisioning'
import { formatApiError } from '@/api/errors'
import { groupTenantsByEnvironment } from './tenantGrouping'
import {
  buildMenuTree, filterTree, countsBySection, sectionsMatchingSearch,
} from './permissionTree'
import { environmentOptions, capTenantOptions } from './capScopeOptions'
import { reconcileSaveGuard } from './reconcileGuard'
import { sensitivityBadge } from './sensitivityBadge'
import { PermissoesComGateDeFuncao } from '@/components/admin/PermissoesComGateDeFuncao'
import { acoesGatesQuery } from '@/api/roles'
import { useAuthStore, useCan } from '@/store/auth'
import { tenantsUnderOus } from '@/utils/ouScope'
import { selectableUsers, type MixedUserRow } from '@/utils/realUsers'

interface UserRow extends MixedUserRow { id: number | null; email: string; full_name: string; tenants: { id: number; name: string }[] }

type TenantRow = { id: number; name: string; environment: string; ou_id?: string | null }
type ScopeMode = 'all' | 'ou'

/** Linha de restrição (cap) editável no modal de template. */
type CapRow = { scopeKind: 'environment' | 'tenant'; scopeValue: string; permissionIds: number[] }

// ── Grid de permissões alinhado ao menu (seção → item → ações) ───────────────

function SensitivityBadge({ sensitivity }: { sensitivity: PermissionSensitivity }) {
  const cfg = sensitivityBadge(sensitivity)
  return (
    <Badge size="xs" variant={cfg.variant} color={cfg.color} styles={{ root: { fontWeight: 600, fontSize: 9, textTransform: 'none' } }}>
      {cfg.label}
    </Badge>
  )
}

function PermissionGrid({
  allPermissions,
  resources,
  selectedIds,
  onChange,
  onAdminFullToggle,
  disabled,
  isAdmin,
}: {
  allPermissions: Permission[]
  resources: PermissionResourceMeta[]
  selectedIds: Set<number>
  onChange: (ids: Set<number>) => void
  onAdminFullToggle?: (selected: boolean) => void
  disabled?: boolean
  isAdmin: boolean
}) {
  const [search, setSearch] = useState('')

  // Quais permissoes ainda exigem FUNCAO depois de concedidas. Derivado do codigo
  // (`role_gates`), porque marcar a caixa aqui nao basta nessas: a funcao e' cobrada
  // num portao anterior. Sem o selo, conceder e ver negado nao tem explicacao na tela.
  const gatesQuery = useQuery(acoesGatesQuery)
  const funcoesPorPermissao = gatesQuery.data?.permissoes_que_exigem_funcao ?? {}

  const adminFullPerm = allPermissions.find((p) => p.resource === 'admin' && p.action === 'full')
  const hasAdminFull = adminFullPerm ? selectedIds.has(adminFullPerm.id) : false

  const toggle = (id: number) => {
    if (disabled) return
    const perm = allPermissions.find((p) => p.id === id)
    // Ao selecionar admin:full → selecionar TODAS as permissoes
    if (perm?.resource === 'admin' && perm?.action === 'full') {
      if (!isAdmin) return
      if (!selectedIds.has(id)) {
        onChange(new Set(allPermissions.map((p) => p.id)))
        onAdminFullToggle?.(true)
      } else {
        onChange(new Set())
        onAdminFullToggle?.(false)
      }
      return
    }
    const next = new Set(selectedIds)
    next.has(id) ? next.delete(id) : next.add(id)
    onChange(next)
  }

  const tree = useMemo(() => buildMenuTree(allPermissions, resources), [allPermissions, resources])

  const q = search.trim().toLowerCase()
  const filteredTree = useMemo(() => filterTree(tree, q), [tree, q])
  // Contadores da árvore INTEIRA, nunca da filtrada: a trilha responde "onde tem
  // permissão marcada", e com a busca ativa um "2/14" que virasse "1/1" mentiria.
  const counts = useMemo(() => countsBySection(tree, selectedIds), [tree, selectedIds])
  const comResultado = useMemo(() => sectionsMatchingSearch(tree, q), [tree, q])

  const [activeSection, setActiveSection] = useState<string | null>(null)
  const secaoAtiva = activeSection ?? filteredTree[0]?.section ?? null

  // Busca cujo resultado está noutra seção: salta para a primeira que tem. Sem
  // isto, a trilha mostraria "nenhuma permissão" na seção ativa enquanto o
  // resultado existe ao lado — o jeito mais fácil de piorar a busca trocando
  // accordion por trilha.
  useEffect(() => {
    if (!q || !secaoAtiva) return
    if (!comResultado.has(secaoAtiva) && filteredTree.length > 0) {
      setActiveSection(filteredTree[0].section)
    }
  }, [q, secaoAtiva, comResultado, filteredTree])

  const secaoExibida = filteredTree.find((sec) => sec.section === secaoAtiva) ?? filteredTree[0]

  return (
    <Stack gap="xs">
      <TextInput
        size="xs"
        placeholder="Buscar por seção, item, permissão ou descrição..."
        leftSection={<IconSearch size={14} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />

      {/* Legenda dos selos de sensibilidade */}
      <Group gap="sm">
        <SensitivityBadge sensitivity="read" />
        <SensitivityBadge sensitivity="write" />
        <SensitivityBadge sensitivity="sensitive" />
        <SensitivityBadge sensitivity="critical" />
        {gatesQuery.isError && (
          <Text size="xs" c="var(--mantine-color-red-6)">
            Não foi possível carregar quais permissões exigem função — os selos abaixo
            estão incompletos.
          </Text>
        )}
      </Group>

      {filteredTree.length === 0 ? (
        <Text size="xs" c="var(--ai-text-muted)">Nenhuma permissão corresponde à busca.</Text>
      ) : (
        <Group align="flex-start" gap="sm" wrap="nowrap" style={{ opacity: disabled ? 0.7 : 1 }}>
          {/* Trilha vertical: aguenta os rótulos inteiros com o contador ao lado,
              sem abreviar nem exigir rolagem horizontal como a régua de abas. */}
          <Stack
            gap={2}
            style={{
              width: 210, flexShrink: 0, paddingRight: 8,
              borderRight: '1px solid var(--ai-border-light)',
            }}
          >
            {filteredTree.map((sec) => {
              const c = counts.get(sec.section) ?? { selected: 0, total: 0 }
              const ativa = sec.section === secaoExibida?.section
              const casa = q !== '' && comResultado.has(sec.section)
              return (
                <UnstyledButton
                  key={sec.section}
                  onClick={() => setActiveSection(sec.section)}
                  style={{
                    padding: '6px 8px', borderRadius: 4,
                    backgroundColor: ativa ? 'var(--ai-body-bg)' : 'transparent',
                    borderLeft: `2px solid ${ativa ? 'var(--ai-brand-verde)' : 'transparent'}`,
                  }}
                >
                  <Group gap={6} justify="space-between" wrap="nowrap">
                    <Group gap={4} wrap="nowrap">
                      {/* Ponto verde = esta seção tem resultado para a busca. É o que
                          substitui o accordion abrindo todas as seções que casavam. */}
                      {casa && (
                        <Box style={{
                          width: 5, height: 5, borderRadius: '50%',
                          backgroundColor: 'var(--ai-brand-verde)', flexShrink: 0,
                        }} />
                      )}
                      <Text
                        size="xs"
                        fw={ativa ? 700 : 600}
                        c={ativa ? 'var(--ai-text-primary)' : 'var(--ai-text-secondary)'}
                      >
                        {sec.section}
                      </Text>
                    </Group>
                    <Text
                      size="xs"
                      fw={600}
                      c={c.selected > 0 ? 'var(--ai-brand-verde)' : 'var(--ai-text-muted)'}
                      style={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
                    >
                      {c.selected}/{c.total}
                    </Text>
                  </Group>
                </UnstyledButton>
              )
            })}
          </Stack>

          <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
            {(secaoExibida?.items ?? []).map((item) => {
              const ItemIcon = iconForKey(item.icon_key)
              return (
                <Box
                  key={item.key}
                  style={{ padding: '8px 10px', borderRadius: 4, border: '1px solid var(--ai-border-light)', backgroundColor: 'var(--ai-body-bg)' }}
                >
                  <Group gap={6} mb={2} align="center">
                    <ItemIcon size={15} style={{ color: 'var(--ai-text-secondary)' }} />
                    <Text fw={700} size="xs" c="var(--ai-text-primary)">{item.menu_item}</Text>
                  </Group>
                  {item.description && (
                    <Text size="xs" c="var(--ai-text-muted)" mb={8}>{item.description}</Text>
                  )}
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={8} verticalSpacing={8}>
                    {item.perms.map((p) => {
                      const isAdminFullPerm = p.resource === 'admin' && p.action === 'full'
                      const lockedAdminFull = isAdminFullPerm && !isAdmin
                      const row = (
                        <Box>
                          <Group gap={8} align="center" wrap="nowrap">
                            <Checkbox
                              label={permissionLabel(p)}
                              checked={selectedIds.has(p.id)}
                              onChange={() => toggle(p.id)}
                              disabled={disabled || lockedAdminFull || (hasAdminFull && !isAdminFullPerm)}
                              size="xs"
                              styles={{ label: { fontSize: 12, fontWeight: 500 } }}
                            />
                            <SensitivityBadge sensitivity={p.sensitivity} />
                            {(funcoesPorPermissao[`${p.resource}:${p.action}`] ?? []).map((f) => (
                              <Tooltip
                                key={f}
                                label={`Concedida, esta permissão só age para quem também tem a função ${f}`}
                              >
                                <Badge size="xs" variant="outline" color="inovareTeal" ff="monospace">
                                  + {f}
                                </Badge>
                              </Tooltip>
                            ))}
                          </Group>
                          {p.description && (
                            <Text size="xs" c="var(--ai-text-muted)" ml={26}>{p.description}</Text>
                          )}
                        </Box>
                      )
                      if (lockedAdminFull) {
                        return (
                          <Tooltip key={p.id} label="Somente administradores podem conceder Acesso total">
                            <span>{row}</span>
                          </Tooltip>
                        )
                      }
                      return <Box key={p.id}>{row}</Box>
                    })}
                  </SimpleGrid>
                </Box>
              )
            })}
          </Stack>
        </Group>
      )}
    </Stack>
  )
}

// ── Editor de template ────────────────────────────────────────────────────────

function TemplateModal({
  opened,
  onClose,
  allPermissions,
  resources,
  allTenants,
  ouNodes,
  editing,
  isAdmin,
}: {
  opened: boolean
  onClose: () => void
  allPermissions: Permission[]
  resources: PermissionResourceMeta[]
  allTenants: TenantRow[]
  ouNodes: AwsOuNode[]
  editing: PermissionTemplate | null
  isAdmin: boolean
}) {
  const qc = useQueryClient()
  const capsEnabled = useFeatureFlag('rbac_template_caps_enabled')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [scopeMode, setScopeMode] = useState<ScopeMode>('ou')
  const [selectedOuIds, setSelectedOuIds] = useState<string[]>([])
  const [caps, setCaps] = useState<CapRow[]>([])

  useEffect(() => {
    if (opened) {
      setName(editing?.name ?? '')
      setDescription(editing?.description ?? '')
      setSelectedIds(new Set(editing?.permissions.map((p) => p.id) ?? []))
      setCaps(
        (editing?.caps ?? []).map((c) => ({
          scopeKind: c.scope_kind,
          scopeValue: c.scope_value,
          permissionIds: [...c.permission_ids],
        })),
      )
      // Inicializa o modo de escopo a partir do template em edição:
      //  - all_tenants → "Toda a organização"
      //  - senão → "Por organização (OU)" com ou_ids pré-selecionados (se vierem)
      if (editing?.all_tenants) {
        setScopeMode('all')
        setSelectedOuIds([])
      } else {
        setScopeMode('ou')
        setSelectedOuIds(editing?.ou_ids ?? [])
      }
    }
  }, [opened, editing])

  // Opções de OU = apenas OUs de cliente (kind === 'tenant').
  const customerOus = useMemo(
    () => ouNodes.filter((n) => n.kind === 'tenant'),
    [ouNodes],
  )
  const ouOptions = useMemo(
    () => customerOus.map((n) => ({ value: n.ou_id, label: n.name })),
    [customerOus],
  )

  const allTenantsFlag = scopeMode === 'all'

  // Tenants derivados do escopo selecionado (read-only). Quando "Por OU",
  // resolve o subtree das OUs escolhidas via árvore local. Quando "Toda a
  // organização", a lista é conceitual (todos os ativos) e não é enumerada.
  const derivedTenants = useMemo(
    () => (allTenantsFlag ? [] : tenantsUnderOus(allTenants, ouNodes, selectedOuIds)),
    [allTenantsFlag, allTenants, ouNodes, selectedOuIds],
  )

  // Template legado: sem ou_ids mas com tenants explícitos (lista antiga). Quando
  // estamos no modo OU e ainda não há OU selecionada, mostramos os tenants
  // legados read-only para o admin reconhecer o que será convertido.
  const isLegacyTemplate =
    !allTenantsFlag &&
    selectedOuIds.length === 0 &&
    (editing?.ou_ids?.length ?? 0) === 0 &&
    (editing?.tenants.length ?? 0) > 0

  const saveMutation = useMutation({
    mutationFn: async () => {
      const permIds = Array.from(selectedIds)
      // Escopo org-driven: tenant_ids sempre [] (lista explícita aposentada).
      //  - "Toda a organização" → all_tenants=true, ou_ids=[]
      //  - "Por organização (OU)" → all_tenants=false, ou_ids=[selecionadas]
      const ouIds = allTenantsFlag ? [] : selectedOuIds
      // Caps (restrições) — interseciona cada cap com a base AO VIVO (selectedIds)
      // antes de enviar: ids que sobraram de uma ação adicionada à cap e depois
      // desmarcada da base seriam rejeitados pelo backend (cap_exceeds_base).
      // Descarta linhas que ficam sem escopo ou sem permissão. O backend revalida.
      const capsPayload: TemplateCap[] = caps
        .map((c) => ({
          scope_kind: c.scopeKind,
          scope_value: c.scopeValue,
          permission_ids: c.permissionIds.filter((id) => selectedIds.has(id)),
        }))
        .filter((c) => c.scope_value && c.permission_ids.length > 0)
      // Quando a flag está OFF, `caps` é omitido (undefined) → "caps ausente = sem
      // mudança": o backend preserva as caps existentes em vez de zerá-las.
      const capsArg = capsEnabled ? capsPayload : undefined
      if (editing) {
        await permissionsApi.updateTemplate(editing.id, name, description, permIds, [], allTenantsFlag, ouIds, capsArg)
      } else {
        await permissionsApi.createTemplate(name, description, permIds, [], allTenantsFlag, ouIds, capsArg)
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['permission-templates'] })
      notifications.show({ color: 'teal', title: 'Salvo', message: `Perfil "${name}" salvo.` })
      onClose()
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao salvar.') })
    },
  })

  // ── Restrições (caps): escopos disponíveis e ações da base ──────────────────
  // As opções NÃO derivam do escopo de tenants do perfil (ver capScopeOptions.ts):
  // a cap de ambiente casa por `tenants.environment` e vale em qualquer
  // organização, então o escopo do perfil não entra na conta. Derivando dele,
  // perfil sem OU — o caso de TODOS os perfis de produção — não oferecia
  // ambiente nenhum e a cap de `prd` já existente nem aparecia selecionada.
  const scopeEnvOptions = useMemo(() => environmentOptions(allTenants), [allTenants])
  const scopeTenantOptions = useMemo(
    () => capTenantOptions(editing?.tenants ?? [], allTenants),
    [editing, allTenants],
  )
  // Ações da BASE (perms selecionadas) — restrição nunca concede além da base.
  const basePerms = useMemo(
    () => allPermissions.filter((p) => selectedIds.has(p.id)),
    [allPermissions, selectedIds],
  )
  // Preset "Só leitura": classificação CONSERVADORA por código de ação — só marca
  // o que é claramente leitura (read*/view*/list*) + docker_ps ("Ver containers").
  // Nunca marca verbo mutante (erro seguro: no máximo deixa uma leitura de fora).
  // export/athena_query ficam de fora de propósito (sensíveis / exfiltração).
  const isReadPerm = (p: { action: string }) =>
    /^(read|view|list)(_|$)/.test(p.action) || p.action === 'docker_ps'
  const readBaseIds = useMemo(
    () => basePerms.filter(isReadPerm).map((p) => p.id),
    [basePerms],
  )

  // ── Fusível do save ─────────────────────────────────────────────────────────
  // Salvar dispara sync TOTAL do `user_tenants` de quem tem este perfil como
  // global (`api.py:8686`), mesmo num save que só mexeu numa restrição — foi
  // assim que um save removeu 633 acessos em 21/08/2026. Pergunta antes, com o
  // `dry_run` que o endpoint já oferece. Template novo não tem usuário vinculado,
  // então não há o que conferir.
  const [preflighting, setPreflighting] = useState(false)
  const handleSave = async () => {
    if (!editing) {
      saveMutation.mutate()
      return
    }
    setPreflighting(true)
    let preview: Awaited<ReturnType<typeof permissionsApi.reconcileTenantScopeDryRun>> = null
    try {
      preview = await permissionsApi.reconcileTenantScopeDryRun(editing.id)
    } catch {
      preview = null // trata como "não deu para conferir", nunca como "pode salvar"
    } finally {
      setPreflighting(false)
    }
    const guard = reconcileSaveGuard(preview)
    if (guard.kind === 'ok') {
      saveMutation.mutate()
      return
    }
    modals.openConfirmModal({
      title: guard.kind === 'warn' ? 'Este save remove acesso' : 'Impacto não verificado',
      children: (
        <Alert color={guard.kind === 'warn' ? 'red' : 'yellow'} variant="light">
          {guard.message}
        </Alert>
      ),
      labels: { confirm: 'Salvar mesmo assim', cancel: 'Cancelar' },
      confirmProps: { color: guard.kind === 'warn' ? 'red' : 'yellow' },
      onConfirm: () => saveMutation.mutate(),
    })
  }

  const updateCap = (idx: number, patch: Partial<CapRow>) =>
    setCaps((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)))

  const toggleCapPerm = (idx: number, permId: number) =>
    setCaps((prev) =>
      prev.map((c, i) => {
        if (i !== idx) return c
        const has = c.permissionIds.includes(permId)
        return {
          ...c,
          permissionIds: has
            ? c.permissionIds.filter((id) => id !== permId)
            : [...c.permissionIds, permId],
        }
      }),
    )

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={editing ? 'Editar template' : 'Novo template'}
      size="xl"
    >
      <Stack gap="sm">
        <TextInput label="Nome" value={name} onChange={(e) => setName(e.currentTarget.value)} required />
        <Textarea label="Descrição" value={description} onChange={(e) => setDescription(e.currentTarget.value)} rows={2} />

        <Divider label="Escopo de acesso (organização)" labelPosition="left" />
        <Stack gap={6}>
          <Group gap={4} align="center">
            <Text size="sm" fw={500}>Como este perfil enxerga os tenants?</Text>
            <Tooltip
              label="O escopo é definido pela organização (AWS Organizations). Contas que entram ou saem de uma OU passam a refletir automaticamente no perfil — não há lista manual de tenants."
              multiline
              w={320}
              withArrow
              position="top"
            >
              <ActionIcon variant="transparent" size="xs" color="gray" aria-label="Informação sobre escopo">
                <IconInfoCircle size={13} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <SegmentedControl
            fullWidth
            value={scopeMode}
            onChange={(v) => setScopeMode(v as ScopeMode)}
            data={[
              { value: 'all', label: 'Toda a organização' },
              { value: 'ou', label: 'Por organização (OU)' },
            ]}
          />

          {allTenantsFlag ? (
            <Alert color="blue" variant="light" icon={<IconBuildingCommunity size={16} />}>
              Este perfil cobre <strong>todos os tenants ativos</strong> da organização. Contas
              criadas no futuro entram automaticamente.
            </Alert>
          ) : (
            <MultiSelect
              label="Organizações (OUs de cliente)"
              description="Os tenants são derivados do subtree das OUs selecionadas."
              placeholder={customerOus.length === 0 ? 'Nenhuma OU de cliente disponível' : 'Selecionar OUs...'}
              data={ouOptions}
              value={selectedOuIds}
              onChange={setSelectedOuIds}
              searchable
              clearable
              disabled={customerOus.length === 0}
            />
          )}

          {/* Lista derivada read-only de tenants resolvidos pelo escopo. */}
          {!allTenantsFlag && (
            <Box>
              {isLegacyTemplate ? (
                <Stack gap={4}>
                  <Alert color="yellow" variant="light" icon={<IconInfoCircle size={16} />}>
                    Perfil legado — selecione uma OU para converter o escopo. Tenants atuais (lista
                    explícita antiga):
                  </Alert>
                  <Group gap={4}>
                    {editing?.tenants.map((tn) => (
                      <Badge key={tn.id} size="xs" variant="outline" color="gray" styles={{ root: { fontWeight: 600, fontSize: 9 } }}>
                        {tn.name}
                      </Badge>
                    ))}
                  </Group>
                </Stack>
              ) : (
                <Stack gap={4}>
                  <Text size="xs" c="var(--ai-text-muted)">
                    Tenants deste perfil (da organização) — atualiza automaticamente quando contas
                    entram/saem da OU.
                  </Text>
                  {derivedTenants.length > 0 ? (
                    <Group gap={4}>
                      {derivedTenants.map((tn) => (
                        <Badge key={tn.id} size="xs" variant="light" color="cyan" styles={{ root: { fontWeight: 600, fontSize: 9 } }}>
                          {`[${tn.environment.toUpperCase()}] ${tn.name}`}
                        </Badge>
                      ))}
                    </Group>
                  ) : (
                    <Text size="xs" c="var(--ai-text-muted)">
                      {selectedOuIds.length === 0
                        ? 'Selecione ao menos uma OU para ver os tenants resolvidos.'
                        : 'Nenhum tenant nas OUs selecionadas (ainda).'}
                    </Text>
                  )}
                </Stack>
              )}
            </Box>
          )}
        </Stack>

        <Divider label="Permissões" labelPosition="left" />
        <PermissionGrid
          allPermissions={allPermissions}
          resources={resources}
          selectedIds={selectedIds}
          onChange={setSelectedIds}
          isAdmin={isAdmin}
          onAdminFullToggle={(selected) => {
            // admin:full conceitualmente cobre toda a organização.
            if (selected) {
              setScopeMode('all')
              setSelectedOuIds([])
            }
          }}
        />

        {capsEnabled && (
          <>
            <Divider label="Restrições por ambiente/tenant (opcional)" labelPosition="left" />
            <Text size="xs" c="var(--ai-text-muted)">
              Sem restrição, o perfil concede a base em todos os tenants do escopo. Adicione uma
              restrição para LIMITAR as ações num ambiente ou tenant específico (nunca concede além
              da base). Precedência: tenant &gt; ambiente &gt; base.
            </Text>

            {basePerms.length === 0 && (
              <Alert color="yellow" variant="light" icon={<IconInfoCircle size={16} />}>
                Selecione ao menos uma permissão na base acima para poder restringir.
              </Alert>
            )}

            {caps.map((cap, idx) => (
              <Paper
                key={idx}
                withBorder
                p="sm"
                radius="md"
                style={{ borderColor: 'var(--ai-border-light)', backgroundColor: 'var(--ai-body-bg)' }}
              >
                <Stack gap="xs">
                  <Group gap="xs" align="flex-end" wrap="nowrap">
                    <Select
                      label="Tipo"
                      size="xs"
                      w={130}
                      allowDeselect={false}
                      data={[
                        { value: 'environment', label: 'Ambiente' },
                        { value: 'tenant', label: 'Tenant' },
                      ]}
                      value={cap.scopeKind}
                      onChange={(v) =>
                        v && updateCap(idx, { scopeKind: v as CapRow['scopeKind'], scopeValue: '' })
                      }
                    />
                    <Select
                      label={cap.scopeKind === 'environment' ? 'Ambiente' : 'Tenant'}
                      size="xs"
                      style={{ flex: 1 }}
                      searchable
                      placeholder={
                        cap.scopeKind === 'environment'
                          ? scopeEnvOptions.length === 0
                            ? 'Nenhum ambiente disponível'
                            : 'Selecionar ambiente...'
                          : scopeTenantOptions.length === 0
                            ? 'Nenhum tenant disponível'
                            : 'Selecionar tenant...'
                      }
                      // Só no ambiente: é o alcance que surpreende. A cap casa por
                      // `tenants.environment` (rbac.py:1209) e não olha organização.
                      description={
                        cap.scopeKind === 'environment'
                          ? 'Vale para todo tenant deste ambiente, em qualquer organização.'
                          : undefined
                      }
                      data={cap.scopeKind === 'environment' ? scopeEnvOptions : scopeTenantOptions}
                      value={cap.scopeValue || null}
                      onChange={(v) => updateCap(idx, { scopeValue: v ?? '' })}
                      disabled={
                        cap.scopeKind === 'environment'
                          ? scopeEnvOptions.length === 0
                          : scopeTenantOptions.length === 0
                      }
                    />
                    <Tooltip label="Remover restrição">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        aria-label="Remover restrição"
                        onClick={() => setCaps((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>

                  <Group justify="space-between" align="center" gap={6} wrap="nowrap">
                    <Text size="xs" fw={600} c="var(--ai-text-secondary)">
                      Ações permitidas neste escopo (subconjunto da base):
                    </Text>
                    <Group gap={8} wrap="wrap" justify="flex-end" style={{ flexShrink: 0 }}>
                      <Text size="xs" c="var(--ai-text-muted)">
                        {cap.permissionIds.length}/{basePerms.length}
                      </Text>
                      <Tooltip
                        label="Marca só as ações de leitura (Visualizar/Listar/Ler). Revise antes de salvar."
                        withArrow
                        position="top"
                        multiline
                        w={240}
                      >
                        <Button
                          variant="light"
                          size="compact-xs"
                          disabled={readBaseIds.length === 0}
                          onClick={() => updateCap(idx, { permissionIds: readBaseIds })}
                        >
                          Só leitura
                        </Button>
                      </Tooltip>
                      <Button
                        variant="subtle"
                        size="compact-xs"
                        onClick={() => updateCap(idx, { permissionIds: basePerms.map((p) => p.id) })}
                      >
                        Marcar todos
                      </Button>
                      <Button
                        variant="subtle"
                        size="compact-xs"
                        color="gray"
                        onClick={() => updateCap(idx, { permissionIds: [] })}
                      >
                        Desmarcar todos
                      </Button>
                    </Group>
                  </Group>
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={4} verticalSpacing={4}>
                    {basePerms.map((p) => (
                      <Checkbox
                        key={p.id}
                        size="xs"
                        label={p.label || `${p.resource}:${p.action}`}
                        checked={cap.permissionIds.includes(p.id)}
                        onChange={() => toggleCapPerm(idx, p.id)}
                        styles={{ label: { fontSize: 12 } }}
                      />
                    ))}
                  </SimpleGrid>
                </Stack>
              </Paper>
            ))}

            <Button
              variant="light"
              size="xs"
              leftSection={<IconPlus size={14} />}
              disabled={basePerms.length === 0}
              onClick={() =>
                setCaps((prev) => [
                  ...prev,
                  { scopeKind: 'environment', scopeValue: '', permissionIds: [] },
                ])
              }
              style={{ alignSelf: 'flex-start' }}
            >
              Adicionar restrição
            </Button>
          </>
        )}

        <Group justify="flex-end" mt="xs">
          <Button variant="subtle" onClick={onClose}>Cancelar</Button>
          <Button loading={saveMutation.isPending || preflighting} disabled={!name.trim() || selectedIds.size === 0} onClick={handleSave}>
            Salvar
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

// ── Modal de aplicação em lote ────────────────────────────────────────────────

function BatchApplyModal({
  opened,
  onClose,
  template,
  users,
  allTenants,
}: {
  opened: boolean
  onClose: () => void
  template: PermissionTemplate | null
  users: UserRow[]
  allTenants: { id: number; name: string; environment: string }[]
}) {
  const qc = useQueryClient()
  const [step, setStep] = useState<1 | 2>(1)
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  const [selectedTenantIds, setSelectedTenantIds] = useState<number[]>([])
  const [tenantSearch, setTenantSearch] = useState('')
  const [loading, setLoading] = useState(false)

  const isAdminFull = template?.permissions.some(p => p.resource === 'admin' && p.action === 'full') ?? false

  const handleClose = () => {
    setStep(1)
    setSelectedUserIds([])
    setSelectedTenantIds([])
    setTenantSearch('')
    onClose()
  }

  useEffect(() => {
    if (opened) {
      setStep(1)
      setSelectedUserIds([])
      setSelectedTenantIds([])
      setTenantSearch('')
    }
  }, [opened])

  const handleSubmit = async () => {
    if (!template) return
    setLoading(true)
    try {
      await Promise.all(
        selectedUserIds.map(uid =>
          permissionsApi.applyTemplateWithScope(
            Number(uid),
            template.id,
            isAdminFull ? [] : selectedTenantIds,
          )
        )
      )
      qc.invalidateQueries({ queryKey: ['user-permissions'] })
      notifications.show({
        color: 'teal',
        title: 'Perfil aplicado',
        message: `"${template.name}" aplicado a ${selectedUserIds.length} usuário(s).`,
      })
      handleClose()
    } catch (err: any) {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao aplicar.') })
    } finally {
      setLoading(false)
    }
  }

  // `/v1/users` mistura cadastros PENDENTES (`id: null`) — ver
  // utils/realUsers. Dois deles gerariam duas opcoes de value "null" e o
  // Mantine derruba a tela.
  const userData = selectableUsers(users).map(u => ({
    value: String(u.id),
    label: `${u.full_name || u.email} (${u.email})`,
  }))

  // UX-M2: agrupa por AMBIENTE (campo real), não por nome derivado. Filtra pela busca.
  const tenantGroups = groupTenantsByEnvironment(allTenants, tenantSearch)

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`Aplicar "${template?.name}" em lote`}
      size="md"
    >
      <Stack gap="sm">
        {/* Indicador de progresso */}
        <Group gap="xs" mb={4}>
          <Badge variant={step === 1 ? 'filled' : 'light'} color="teal" size="sm">
            1. Usuários
          </Badge>
          <Text size="xs" c="var(--ai-text-muted)">→</Text>
          <Badge variant={step === 2 ? 'filled' : 'light'} color="teal" size="sm">
            2. Tenants
          </Badge>
        </Group>

        {step === 1 && (
          <>
            <Text size="xs" c="var(--ai-text-muted)">
              Selecione os usuários que receberão este template.
            </Text>
            <MultiSelect
              label="Usuários"
              placeholder="Buscar usuários..."
              data={userData}
              value={selectedUserIds}
              onChange={setSelectedUserIds}
              searchable
              clearable
              maxDropdownHeight={200}
            />
            <Group justify="flex-end" mt="xs">
              <Button variant="subtle" onClick={handleClose}>Cancelar</Button>
              <Button
                disabled={selectedUserIds.length === 0}
                onClick={() => setStep(2)}
              >
                Próximo →
              </Button>
            </Group>
          </>
        )}

        {step === 2 && (
          <>
            {isAdminFull ? (
              <Alert color="blue" variant="light">
                Perfil com <strong>admin:full</strong> — usuários terão acesso a todos os tenants automaticamente.
              </Alert>
            ) : (
              <>
                <Text size="xs" c="var(--ai-text-muted)">
                  Selecione os tenants que os usuários ({selectedUserIds.length}) terão acesso:
                </Text>
                <TextInput
                  size="xs"
                  placeholder="Buscar tenant ou ambiente..."
                  leftSection={<IconSearch size={13} />}
                  value={tenantSearch}
                  onChange={(e) => setTenantSearch(e.currentTarget.value)}
                />
                <Box style={{ maxHeight: 280, overflowY: 'auto' }}>
                  {tenantGroups.length === 0 ? (
                    <Text size="xs" c="dimmed" py="xs">Nenhum tenant encontrado.</Text>
                  ) : (
                    tenantGroups.map((g) => {
                      const groupIds = g.tenants.map((t) => t.id)
                      const allSel = groupIds.every((id) => selectedTenantIds.includes(id))
                      return (
                        <Box key={g.environment} mb="xs">
                          <Group justify="space-between" align="center" mb={4}>
                            <Text size="xs" fw={700} c="var(--ai-text-muted)" tt="uppercase">
                              {g.environment}
                            </Text>
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              onClick={() =>
                                setSelectedTenantIds((prev) =>
                                  allSel
                                    ? prev.filter((id) => !groupIds.includes(id))
                                    : [...new Set([...prev, ...groupIds])]
                                )
                              }
                            >
                              {allSel ? 'Limpar' : 'Selecionar todos'}
                            </Button>
                          </Group>
                          <SimpleGrid cols={2} spacing={4} verticalSpacing={4}>
                            {g.tenants.map((t) => (
                              <Checkbox
                                key={t.id}
                                size="xs"
                                label={t.name}
                                checked={selectedTenantIds.includes(t.id)}
                                onChange={() =>
                                  setSelectedTenantIds((prev) =>
                                    prev.includes(t.id)
                                      ? prev.filter((id) => id !== t.id)
                                      : [...prev, t.id]
                                  )
                                }
                              />
                            ))}
                          </SimpleGrid>
                        </Box>
                      )
                    })
                  )}
                </Box>
              </>
            )}
            <Group justify="flex-end" mt="xs">
              <Button variant="subtle" onClick={() => setStep(1)}>← Voltar</Button>
              <Button
                color="teal"
                loading={loading}
                disabled={!isAdminFull && selectedTenantIds.length === 0}
                onClick={handleSubmit}
              >
                Aplicar a {selectedUserIds.length} usuário{selectedUserIds.length !== 1 ? 's' : ''}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  )
}

// ── Template Card ────────────────────────────────────────────────────────────

function TemplateCard({ t, onEdit, onDelete, onDuplicate, onPreview, onBatchApply, onUnlinkUser, isAdmin }: {
  t: PermissionTemplate
  onEdit: () => void
  onDelete: () => void
  onDuplicate: () => void
  onPreview: () => void
  onBatchApply: () => void
  onUnlinkUser: (userId: number, userName: string) => void
  isAdmin: boolean
}) {
  const [expandedSection, setExpandedSection] = useState<'permissions' | 'users' | 'tenants' | null>(null)
  // Escopo classificado pelo BACKEND. A heuristica antiga derivava de
  // `t.tenants.length`, campo que nunca inclui os tenants resolvidos por
  // `ou_ids` — perfil por OU aparecia "0 tenants" e "Escopo global" ao mesmo
  // tempo.
  const scope = templateScopeSummary(t)
  // RBAC config (criar/editar/deletar templates) e admin-only — backend bloqueia tambem.
  // Aplica em TODOS os templates, nao so nos com admin:full.
  const editLocked = !isAdmin
  // Aplicar em lote / desvincular usuario do template Administrador concede ou
  // revoga admin:full — bloqueado pelo guard de v1.32.7. Esconde a UI quando
  // o caller nao e admin E o template e admin.
  const isAdminTemplate = t.permissions.some((p) => p.resource === 'admin' && p.action === 'full')
  const adminGrantLocked = isAdminTemplate && !isAdmin

  const toggleSection = (section: 'permissions' | 'users' | 'tenants') => {
    setExpandedSection(expandedSection === section ? null : section)
  }

  return (
    <Card
      radius="md"
      withBorder
      p={0}
      style={{
        borderColor: 'var(--ai-border-light)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--ai-border-light)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Text fw={700} size="sm" c="var(--ai-text-primary)">{t.name}</Text>
          {t.description && (
            <Text size="xs" c="var(--ai-text-muted)" mt={2} lineClamp={1}>{t.description}</Text>
          )}
        </Box>
        <Group gap={2}>
          {editLocked && (
            <Tooltip label="Somente administradores podem editar templates">
              <IconLock size={14} style={{ color: 'var(--ai-text-muted)' }} />
            </Tooltip>
          )}
          {!adminGrantLocked && (
            <Tooltip label="Aplicar em lote">
              <ActionIcon size="sm" variant="subtle" color="teal" onClick={onBatchApply}>
                <IconUsers size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          {!editLocked && (
            <>
              <Tooltip label="Editar">
                <ActionIcon size="sm" variant="subtle" onClick={onEdit}>
                  <IconEdit size={14} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Duplicar (copia só as permissões)">
                <ActionIcon size="sm" variant="subtle" color="blue" onClick={onDuplicate}>
                  <IconCopy size={14} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Remover">
                <ActionIcon size="sm" variant="subtle" color="red" onClick={onDelete}>
                  <IconTrash size={14} />
                </ActionIcon>
              </Tooltip>
            </>
          )}
          <Tooltip label="Visualizar como este perfil">
            <ActionIcon size="sm" variant="subtle" color="orange" onClick={onPreview}>
              <IconEye size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Box>

      {/* Stats */}
      <Box style={{ padding: '10px 16px' }}>
        <Group gap="lg">
          <Group gap={6} onClick={() => toggleSection('permissions')} style={{ cursor: 'pointer' }}>
            <IconShieldCheck size={14} style={{ color: expandedSection === 'permissions' ? 'var(--ai-brand-verde)' : 'var(--ai-text-muted)' }} />
            <Text size="xs" fw={600} c="var(--ai-text-secondary)">
              {t.permissions.length} permissões
            </Text>
          </Group>
          <Group gap={6} onClick={() => toggleSection('users')} style={{ cursor: 'pointer' }}>
            <IconUserCheck size={14} style={{ color: expandedSection === 'users' ? 'var(--ai-brand-verde)' : t.linked_users_count > 0 ? 'var(--ai-brand-verde)' : 'var(--ai-text-muted)' }} />
            <Text size="xs" fw={600} c="var(--ai-text-secondary)">
              {t.linked_users_count} usuário{t.linked_users_count !== 1 ? 's' : ''}
            </Text>
          </Group>
          <Group gap={6} onClick={() => toggleSection('tenants')} style={{ cursor: 'pointer' }}>
            <IconUsers
              size={14}
              style={{
                color: expandedSection === 'tenants'
                  ? 'var(--ai-brand-verde)'
                  : scope.tone === 'warn'
                    ? 'var(--mantine-color-yellow-6)'
                    : 'var(--ai-brand-verde)',
              }}
            />
            <Text size="xs" fw={600} c={scope.tone === 'warn' ? 'yellow.7' : 'var(--ai-text-secondary)'}>
              {scope.label}
            </Text>
          </Group>
        </Group>

        {/* Expandable: Permissões */}
        {expandedSection === 'permissions' && (
          <Box mt={10}>
            <Group gap={4}>
              {t.permissions.map((p) => (
                <Badge key={p.id} size="xs" variant="light" color="gray" styles={{ root: { fontWeight: 600, textTransform: 'uppercase', fontSize: 9, letterSpacing: '0.02em' } }}>
                  {p.resource}:{p.action}
                </Badge>
              ))}
            </Group>
          </Box>
        )}

        {/* Expandable: Usuários */}
        {expandedSection === 'users' && (
          <Box mt={10}>
            {t.linked_users.length > 0 ? (
              <Group gap={4}>
                {t.linked_users.map((u) => (
                  <Badge
                    key={u.id}
                    size="xs"
                    variant="light"
                    color="blue"
                    styles={{ root: { fontWeight: 600, fontSize: 9, paddingRight: adminGrantLocked ? undefined : 2 } }}
                    rightSection={adminGrantLocked ? undefined : (
                      <Tooltip label={`Remover ${u.full_name || u.email} do template`}>
                        <ActionIcon
                          size="xs"
                          variant="transparent"
                          color="blue"
                          aria-label={`Remover ${u.full_name || u.email}`}
                          onClick={() => onUnlinkUser(u.id, u.full_name || u.email)}
                        >
                          <IconX size={10} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  >
                    {u.full_name}
                  </Badge>
                ))}
              </Group>
            ) : (
              <Text size="xs" c="var(--ai-text-muted)">Nenhum usuário vinculado</Text>
            )}
          </Box>
        )}

        {/* Expandable: Tenants */}
        {expandedSection === 'tenants' && (
          <Box mt={10}>
            {t.scope_kind === 'all_tenants' ? (
              <Badge size="sm" variant="filled" color="teal" styles={{ root: { fontWeight: 600 } }}>
                Todos os tenants ativos (atuais e futuros)
              </Badge>
            ) : (t.effective_tenants?.length ?? 0) > 0 ? (
              <Group gap={4}>
                {t.effective_tenants!.map((tn) => (
                  <Badge key={tn.id} size="xs" variant="outline" color="cyan" styles={{ root: { fontWeight: 600, fontSize: 9 } }}>
                    {tn.name}
                  </Badge>
                ))}
              </Group>
            ) : t.scope_kind === 'none' && (t.tenants?.length ?? 0) > 0 ? (
              /* Sem escopo proprio, mas aplicado POR ORGANIZACAO: o alcance vem
                 de organization_members, e has_permission decide pelo template
                 da org sem olhar a lista propria (rbac.py:1580-1615). Dizer
                 "nao concede acesso" aqui era falso. */
              <Stack gap={4}>
                <Text size="xs" c="var(--ai-text-muted)">
                  Sem escopo próprio — alcance vem das organizações onde o perfil é usado:
                </Text>
                <Group gap={4}>
                  {t.tenants.map((tn) => (
                    <Badge key={tn.id} size="xs" variant="outline" color="cyan" styles={{ root: { fontWeight: 600, fontSize: 9 } }}>
                      {tn.name}
                    </Badge>
                  ))}
                </Group>
              </Stack>
            ) : t.scope_kind === 'none' ? (
              <Text size="xs" c="yellow.7" fw={600}>
                Nenhum tenant — este perfil não concede acesso a tenant nenhum.
              </Text>
            ) : (
              /* scope_kind ausente: backend anterior a 2026-08. NAO inventar
                 "escopo global" aqui — era o proprio bug. */
              <Text size="xs" c="var(--ai-text-muted)">Escopo não informado pelo servidor.</Text>
            )}
          </Box>
        )}
      </Box>
    </Card>
  )
}

// ── Página principal ─────────────────────────────────────────────────────────

export function Permissoes() {
  const { user, selectedTenantId, enterPreview } = useAuthStore()
  const can = useCan()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [templateModalOpened, { open: openTmpl, close: closeTmpl }] = useDisclosure(false)
  const [batchModalOpened, { open: openBatch, close: closeBatch }] = useDisclosure(false)
  const [editingTemplate, setEditingTemplate] = useState<PermissionTemplate | null>(null)
  const [batchTemplate, setBatchTemplate] = useState<PermissionTemplate | null>(null)
  // Duplicar template: guarda a origem + o nome do clone (pré-preenchido).
  const [dupTemplate, setDupTemplate] = useState<PermissionTemplate | null>(null)
  const [dupName, setDupName] = useState('')

  if (!can('usuarios', 'manage')) return <Navigate to="/" replace />
  const isAdmin = can('admin', 'full')

  const { data: permissionsData } = useQuery({
    queryKey: ['permissions-catalog'],
    queryFn: () => permissionsApi.listPermissions(),
  })

  const { data: templatesData, isLoading: loadingTmpl } = useQuery({
    queryKey: ['permission-templates'],
    queryFn: () => permissionsApi.listTemplates(),
  })

  const { data: usersData } = useQuery<{ items: UserRow[] }>({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await api.get('/v1/users')
      return res.data
    },
  })

  const { data: tenantsData } = useQuery<{ items: TenantRow[] }>({
    queryKey: ['tenants-all'],
    queryFn: async () => {
      const res = await api.get('/v1/admin/tenants')
      return res.data
    },
    enabled: can('usuarios', 'manage'),
  })

  // include_legacy=false: OU removida da AWS nao pode ser oferecida como escopo
  // de template — escolher uma seria gravar um escopo que nunca vai casar com
  // nada. Mesma decisao ja tomada em /admin/tenants (MR !1325).
  //
  // Medido em producao em 26/08/2026 antes de mudar: `permission_template_ous`
  // tem 3 linhas no total e ZERO apontam para lapide, entao nenhum template
  // existente perde escopo com isto. Por isso NAO ha aviso de "escopo orfao"
  // aqui: seria tela para um caso que nao existe. Ver TASK-10148.
  const { data: ouTreeData } = useQuery({
    queryKey: ['aws-ou-tree', false],
    queryFn: () => awsOrgProvisioningApi.getOuTree(false),
    enabled: isAdmin,
  })

  const allTenants = tenantsData?.items ?? []
  const ouNodes = ouTreeData?.nodes ?? []

  const deleteTmplMutation = useMutation({
    mutationFn: (id: number) => permissionsApi.deleteTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['permission-templates'] })
      notifications.show({ color: 'teal', title: 'Removido', message: 'Perfil excluído.' })
    },
    onError: (err: any) => {
      // 409 = delete condicional bloqueado (associado a org / em uso / roteamento).
      const status = err?.response?.status
      const detail = err?.response?.data?.detail
      if (status === 409 && detail && typeof detail === 'object') {
        notifications.show({
          color: 'orange',
          title: 'Não é possível excluir',
          message: detail.message || 'O perfil está em uso e não pode ser excluído.',
          autoClose: 9000,
        })
        return
      }
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao excluir o perfil.') })
    },
  })

  const duplicateTmplMutation = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => permissionsApi.duplicateTemplate(id, name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['permission-templates'] })
      notifications.show({ color: 'teal', title: 'Duplicado', message: 'Perfil duplicado (só as permissões). Ajuste o escopo de tenants/OU no novo perfil.' })
      setDupTemplate(null)
      setDupName('')
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      const msg = detail === 'template_name_exists' ? 'Já existe um template com esse nome.' : 'Falha ao duplicar template.'
      notifications.show({ color: 'red', title: 'Erro', message: msg })
    },
  })

  const unlinkUserMutation = useMutation({
    mutationFn: (userId: number) => permissionsApi.unlinkTemplate(userId),
    onSuccess: (_data, userId) => {
      qc.invalidateQueries({ queryKey: ['permission-templates'] })
      qc.invalidateQueries({ queryKey: ['user-permissions', userId] })
      notifications.show({ color: 'teal', title: 'Removido', message: 'Usuário desvinculado do template.' })
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao desvincular.') })
    },
  })

  const allPermissions = permissionsData?.items ?? []
  const resourcesMeta = permissionsData?.resources ?? []
  const templates = templatesData ?? []
  const users = usersData?.items ?? []

  return (
    <Stack gap="lg">
      {/* ── Templates de perfil ── */}
      <Card radius="md" withBorder p={0} style={{ borderColor: 'var(--ai-border-light)' }}>
        <Box
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid var(--ai-border-light)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Group gap={8}>
            <IconKey size={16} style={{ color: 'var(--ai-brand-verde)' }} />
            <Text fw={700} size="sm" c="var(--ai-text-primary)">Perfis</Text>
            <Badge size="xs" variant="light" color="gray">{templates.length}</Badge>
          </Group>
          {isAdmin ? (
            <Button
              size="xs"
              leftSection={<IconPlus size={13} />}
              variant="light"
              onClick={() => { setEditingTemplate(null); openTmpl() }}
            >
              Novo template
            </Button>
          ) : (
            <Tooltip label="Somente administradores podem criar templates">
              <span><IconLock size={14} style={{ color: 'var(--ai-text-muted)' }} /></span>
            </Tooltip>
          )}
        </Box>

        <Box p="md">
          {loadingTmpl ? <Skeleton height={80} /> : (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="sm">
              {templates.map((t) => (
                <TemplateCard
                  key={t.id}
                  t={t}
                  isAdmin={isAdmin}
                  onEdit={() => { setEditingTemplate(t); openTmpl() }}
                  onDelete={() => modals.openConfirmModal({
                    title: 'Excluir perfil',
                    children: (
                      <Text size="sm">
                        Excluir o perfil <b>{t.name}</b>? A exclusão é <b>definitiva</b>. Não será
                        possível excluir se o perfil estiver associado a uma organização, em uso por
                        usuários ou como destino de roteamento de chamados.
                      </Text>
                    ),
                    labels: { confirm: 'Excluir', cancel: 'Cancelar' },
                    confirmProps: { color: 'red' },
                    onConfirm: () => deleteTmplMutation.mutate(t.id),
                  })}
                  onDuplicate={() => { setDupTemplate(t); setDupName(`${t.name} (cópia)`) }}
                  onBatchApply={() => { setBatchTemplate(t); openBatch() }}
                  onUnlinkUser={(userId, userName) => modals.openConfirmModal({
                    title: 'Remover usuário do perfil',
                    children: (
                      <Text size="sm">
                        Remover <b>{userName}</b> do perfil "{t.name}"? O usuário ficará sem
                        permissões aplicadas.
                      </Text>
                    ),
                    labels: { confirm: 'Remover', cancel: 'Cancelar' },
                    confirmProps: { color: 'red' },
                    onConfirm: () => unlinkUserMutation.mutate(userId),
                  })}
                  onPreview={() => {
                    enterPreview({
                      id: t.id,
                      name: t.name,
                      permissions: t.permissions.map((p) => `${p.resource}:${p.action}`),
                      // Comprimento, nao nulidade: `effective_tenants` vem VAZIA
                      // quando o escopo e `all_tenants` (o backend nao enumera), e
                      // um `??` deixaria a lista vazia — o cap por ambiente nao
                      // teria como casar, porque sai do `environment` do tenant.
                      tenants: t.effective_tenants?.length ? t.effective_tenants : t.tenants,
                      // Sem as caps o preview mostrava a base cheia de um perfil
                      // restrito — mentia na direcao permissiva.
                      caps: (t.caps ?? []).map((c) => ({
                        scope_kind: c.scope_kind,
                        scope_value: c.scope_value,
                        permissions: t.permissions
                          .filter((p) => c.permission_ids.includes(p.id))
                          .map((p) => `${p.resource}:${p.action}`),
                      })),
                    })
                    navigate('/')
                  }}
                />
              ))}
            </SimpleGrid>
          )}
        </Box>
      </Card>

      <PermissoesComGateDeFuncao />

      <TemplateModal
        opened={templateModalOpened}
        onClose={closeTmpl}
        allPermissions={allPermissions}
        resources={resourcesMeta}
        allTenants={allTenants}
        ouNodes={ouNodes}
        editing={editingTemplate}
        isAdmin={isAdmin}
      />

      <BatchApplyModal
        opened={batchModalOpened}
        onClose={closeBatch}
        template={batchTemplate}
        users={users}
        allTenants={allTenants}
      />

      <Modal
        opened={dupTemplate !== null}
        onClose={() => { setDupTemplate(null); setDupName('') }}
        title={`Duplicar template "${dupTemplate?.name ?? ''}"`}
      >
        <Stack gap="sm">
          <Text size="xs" c="dimmed">
            O clone copia <b>apenas as permissões</b>. O escopo de tenants e de OU nasce em branco —
            defina o alcance no novo perfil depois. A flag de roteamento padrão não é copiada.
          </Text>
          <TextInput
            label="Nome do novo template"
            placeholder="Ex: DEV-AIRETAIL (cópia)"
            value={dupName}
            onChange={(e) => setDupName(e.currentTarget.value)}
            withAsterisk
            data-autofocus
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { setDupTemplate(null); setDupName('') }}>Cancelar</Button>
            <Button
              loading={duplicateTmplMutation.isPending}
              disabled={!dupName.trim()}
              onClick={() => dupTemplate && duplicateTmplMutation.mutate({ id: dupTemplate.id, name: dupName.trim() })}
            >
              Duplicar
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
